import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@fieldbook/common/supabase"
import { getSupabaseAuthStorageKey } from "@fieldbook/common/supabaseClient"
import { authStorage } from "@/platform"
import { getAppMeta } from "@fieldbook/common/authClaims"
import { isAuthRetryableFetchError } from "@supabase/supabase-js"
import {
  clearOfflineAuthSnapshot,
  getSessionWithOfflineFallback,
  writeOfflineAuthSnapshot,
} from "@/lib/offlineAuth"
import { useAuthContext } from "@/contexts/auth"
import { unregisterPushDeviceToken } from "@/lib/pushDeviceToken"
import { useNavigate } from "@tanstack/react-router"
import { useRouter } from "@tanstack/react-router"
import * as Sentry from "@sentry/react"
import { usePostHog } from "posthog-js/react"
import { useEffect, useMemo } from "react"
import { withTimeout } from "@/lib/withTimeout"

// The network calls in logout only revoke server-side state, so they are
// bounded: the user is signed out locally without waiting on a slow request.
const LOGOUT_REMOTE_TIMEOUT_MS = 5_000

export const getOrgUser = async (userId: string) => {
  return await supabase
    .from("org_user")
    .select("*, organisation(id, name)")
    .eq("user_id", userId)
    .single()
}

const SESSION_KEY = ["auth", "session"] as const

export const useAuth = () => {
  const { isLoggedIn, setIsLoggedIn } = useAuthContext()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const posthog = usePostHog()
  const router = useRouter()

  // Single source of truth for identity + org + role.
  // The live session is persisted by the platform auth storage. If refresh
  // cannot complete while offline, fall back to a minimal offline snapshot.
  // Subscription-driven updates happen in the AuthProvider.
  const { data: session } = useQuery({
    queryKey: SESSION_KEY,
    queryFn: async () => (await getSessionWithOfflineFallback()).session,
    staleTime: Infinity,
    gcTime: Infinity,
  })

  const user = session?.user ?? null
  const appMeta = getAppMeta(session)

  useEffect(() => {
    if (!session) return
    setIsLoggedIn(true)
  }, [session, setIsLoggedIn])

  // Org/role come from JWT claims populated by the custom access-token hook.
  // Fallback: query org_user for pre-hook sessions; result is cached in the
  // query persister so it survives offline use once fetched.
  const needsFallback = Boolean(user) && !appMeta.org_id
  const { data: fallbackOrgUser } = useQuery({
    queryKey: ["auth", "orgUser"],
    queryFn: async () => {
      if (!user) return null
      const { data, error } = await getOrgUser(user.id)
      if (error) throw new Error("Unable to fetch organisation")
      return data
    },
    enabled: needsFallback,
    staleTime: 60 * 60 * 1000,
  })

  const org = useMemo(
    () =>
      appMeta.org_id
        ? { id: appMeta.org_id, name: appMeta.org_name ?? "" }
        : fallbackOrgUser?.organisation,
    [appMeta, fallbackOrgUser],
  )

  useEffect(() => {
    if (!user || !org) return
    posthog.identify(user.id, { org: org?.name })
  }, [user, org, posthog])

  useEffect(() => {
    Sentry.setUser(
      user ? { id: user.id, email: user.email ?? undefined } : null,
    )
  }, [user])

  const role = appMeta.role ?? fallbackOrgUser?.role

  const login = useMutation({
    mutationFn: async ({
      email,
      password,
    }: {
      email: string
      password: string
    }) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (error) {
        if (isAuthRetryableFetchError(error))
          throw new Error("Unable to connect to server")
        else throw error
      }
      return data
    },
    networkMode: "online",
    retry: false,
    onSuccess: async (data) => {
      Sentry.setUser({ id: data.user.id, email: data.user.email })
      posthog.identify(data.user.id, {
        email: data.user.email,
      })
      posthog.capture("user_logged_in", { email: data.user.email })

      // Seed the session cache immediately so downstream derivations
      // (org, role) are available without waiting on a refetch.
      queryClient.setQueryData(SESSION_KEY, data.session)
      if (data.session) await writeOfflineAuthSnapshot(data.session)

      setIsLoggedIn(true)
    },
  })

  const logout = useMutation({
    // Logout must run offline. The default ("online") pauses the mutation
    // until connectivity returns, so nothing below would clear the session:
    // the user would stay signed in, and a relaunch would still be signed in.
    networkMode: "always",
    mutationFn: async () => {
      // Server-side clean-up only, so it is bounded and best-effort. Both
      // steps need the live session, so they run together and before it is
      // removed:
      // - Remove this device's push token (RLS scopes the delete to the
      //   caller) so a shared device stops delivering the signed-out user's
      //   pushes.
      // - Revoke the refresh token. Offline this fails; the token then
      //   expires server-side per the project's token lifetime.
      await withTimeout(
        Promise.allSettled([
          unregisterPushDeviceToken(),
          supabase.auth.signOut({ scope: "global" }),
        ]),
        LOGOUT_REMOTE_TIMEOUT_MS,
      )
      // From here on, nothing needs the network. What matters is that the
      // user *is* logged out locally, and that must hold offline: the route
      // guard re-authenticates from either of these on the next load.
      await clearOfflineAuthSnapshot()
      // supabase-js removes the stored session only once the server confirms
      // the sign-out. Offline, signOut() returns the fetch error and leaves
      // the session in storage, so the guard would find it and sign the user
      // straight back in (also on the next cold start).
      await authStorage.removeItem(getSupabaseAuthStorageKey())
      // With no stored session this makes no request: it clears the client's
      // remaining state and emits SIGNED_OUT. Best-effort, because the stored
      // session is already gone.
      await supabase.auth.signOut({ scope: "local" })
    },
    onMutate: () => {
      setIsLoggedIn(false)
      posthog.capture("user_logged_out")

      queryClient.setQueriesData({ queryKey: ["auth"] }, null)
    },
    // Navigate only once the stored session is gone (or logout failed). The
    // _private guard falls back to the offline snapshot and the persisted
    // Supabase session, so running it earlier would find them still there,
    // re-seed the session cache and send the user back to Projects.
    onSettled: async () => {
      posthog.reset()

      // Force router to re-run beforeLoad checks with new context
      await router.invalidate()

      // Now navigate - the _private beforeLoad will redirect anyway,
      // but this ensures we go to login
      await navigate({ to: "/auth/login" })
    },
  })

  return {
    user,
    org,
    role,
    getSession: () => supabase.auth.getSession(),
    login,
    logout,
    isLoggedIn,
  }
}
